import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Bell, CheckCircle2, ChevronRight } from 'lucide-react';

import { cn } from '@/lib/utils';
import { useAdminNotifications } from '@/hooks/admin/useAdminNotifications';

/**
 * Header notifications bell (superadmins). Badges with the total count of
 * items needing action across every admin queue; the dropdown lists each
 * queue with its newest items, every row deep-linking to the exact page
 * where the action happens. Data + polling live in useAdminNotifications.
 *
 * Styled for the navy half of the header gradient (white icon), dropdown
 * panel matches the profile menu.
 */
export function NotificationsBell() {
  const [open, setOpen] = useState(false);
  const { data } = useAdminNotifications();

  const total = data?.total ?? 0;
  const categories = data?.categories ?? [];

  return (
    <div className="relative">
      <button
        type="button"
        aria-label={total > 0 ? `Notifications — ${total} items need action` : 'Notifications'}
        aria-expanded={open}
        aria-haspopup="menu"
        onClick={() => setOpen((v) => !v)}
        className={cn(
          'relative w-8 h-8 rounded-lg flex items-center justify-center border backdrop-blur-md',
          'transition-all duration-200 cursor-pointer',
          'border-white/10 bg-white/[0.06] hover:bg-white/[0.12] hover:border-white/15',
        )}
      >
        <Bell size={16} strokeWidth={1.75} className="text-white" />
        {total > 0 && (
          <span
            className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 rounded-full bg-red-600 text-white text-[10px] font-bold flex items-center justify-center border border-white/40"
            aria-hidden
          >
            {total > 99 ? '99+' : total}
          </span>
        )}
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-40" aria-hidden onClick={() => setOpen(false)} />
          <div
            role="menu"
            className="absolute top-full right-0 mt-2 w-[22rem] max-h-[70vh] overflow-y-auto z-50 rounded-xl border border-brix-border bg-brix-surface/95 backdrop-blur-xl shadow-card-hover"
          >
            <div className="px-4 py-3 border-b border-brix-border bg-brix-surface-alt/40">
              <p className="text-sm font-semibold text-brix-ink">Needs attention</p>
              <p className="text-[12px] text-brix-muted mt-0.5">
                {total > 0
                  ? `${total} item${total === 1 ? '' : 's'} waiting on an action`
                  : 'Nothing waiting on you'}
              </p>
            </div>

            {total === 0 ? (
              <div className="px-4 py-6 text-center text-sm text-brix-muted">
                <CheckCircle2 size={20} className="inline-block text-emerald-500 mb-1.5" />
                <p>All caught up — applications, setup, change requests, EDI, audits and payments are clear.</p>
              </div>
            ) : (
              categories.map((c) => (
                <div key={c.key} className="border-b border-brix-border last:border-b-0">
                  <Link
                    to={c.link}
                    role="menuitem"
                    onClick={() => setOpen(false)}
                    className="flex items-center justify-between gap-2 px-4 py-2.5 hover:bg-brix-primary-tint/60 cursor-pointer transition-colors"
                  >
                    <span className="text-sm font-semibold text-brix-ink">{c.label}</span>
                    <span className="inline-flex items-center gap-1 text-brix-primary text-[12px] font-bold">
                      {c.overflow ? `${c.count}+` : c.count}
                      <ChevronRight size={13} />
                    </span>
                  </Link>
                  <ul className="pb-1.5">
                    {c.items.map((it) => (
                      <li key={it.id}>
                        <Link
                          to={it.link}
                          role="menuitem"
                          onClick={() => setOpen(false)}
                          className="block pl-6 pr-4 py-1.5 hover:bg-brix-primary-tint/40 cursor-pointer transition-colors"
                        >
                          <span className="block text-[13px] text-brix-ink truncate">{it.label}</span>
                          {it.sublabel && (
                            <span className="block text-[11px] text-brix-muted truncate">{it.sublabel}</span>
                          )}
                        </Link>
                      </li>
                    ))}
                    {c.count > c.items.length && (
                      <li>
                        <Link
                          to={c.link}
                          onClick={() => setOpen(false)}
                          className="block pl-6 pr-4 py-1.5 text-[12px] text-brix-primary font-semibold hover:underline cursor-pointer"
                        >
                          View all {c.overflow ? `${c.count}+` : c.count} →
                        </Link>
                      </li>
                    )}
                  </ul>
                </div>
              ))
            )}
          </div>
        </>
      )}
    </div>
  );
}
